import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PhoneCall, Loader2, Mail } from "lucide-react";
import PhoneInput from "@/components/PhoneInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
  website: "", // honeypot — must stay empty
};

// Captured once on mount from window.location + document.referrer
function captureAttribution() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const get = (k) => params.get(k) || undefined;
  return {
    utm_source:   get("utm_source"),
    utm_medium:   get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_term:     get("utm_term"),
    utm_content:  get("utm_content"),
    referrer:     document.referrer || undefined,
  };
}

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const attribution = useRef({});

  useEffect(() => {
    attribution.current = captureAttribution();
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Basic client validation (honeypot is intentionally skipped — bots fill it)
    if (!form.name || !form.email || !form.phone || !form.company || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/contact`,
        { ...form, ...attribution.current },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Request received. We'll be in touch within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || "Too many requests. Please wait a moment.");
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Couldn't send your request. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90">
              Contact
            </div>
            <h2
              data-testid="contact-heading"
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white"
            >
              Let&apos;s build your
              <br />
              <span className="zx-hi">next winning team.</span>
            </h2>
            <p className="mt-6 max-w-md text-neutral-200 leading-relaxed">
              Tell us about the role, the mandate or the growth target. A
              partner will respond personally within 24 hours.
            </p>

            <div className="mt-10 space-y-4 text-sm text-neutral-200">
              <a
                href="mailto:mayur@zevnoxsearch.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 group"
              >
                <span className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#D9F99D] group-hover:border-[#D9F99D]/40 group-hover:bg-[#D9F99D]/[0.06] transition-colors">
                  <Mail size={14} />
                </span>
                <span className="text-white group-hover:text-[#D9F99D] transition-colors">
                  mayur@zevnoxsearch.com
                </span>
              </a>
              <div className="flex items-center gap-3">
                <span className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#D9F99D]">
                  <PhoneCall size={14} />
                </span>
                <span>Global callback — London · Dubai · Singapore</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-[#D9F99D] zx-pulse" />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-300">
                  Avg. response · 2h 14m
                </span>
              </div>
            </div>

            <div className="mt-10 relative aspect-[16/10] rounded-2xl overflow-hidden border border-white/8 zx-glow-ring">
              <img
                src="/contact-hero.jpg"
                alt="Building elite teams — global executive network"
                loading="lazy"
                decoding="async"
                width="640"
                height="400"
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  filter:
                    "contrast(1.20) saturate(1.20) brightness(1.04)",
                }}
              />
              {/* Directional key light from top-left */}
              <div
                aria-hidden
                className="absolute inset-0 mix-blend-screen pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 65% 55% at 25% 20%, rgba(255,255,255,0.16), rgba(255,255,255,0) 65%)",
                }}
              />
              {/* Premium central focal lift on interaction point */}
              <div
                aria-hidden
                className="absolute inset-0 mix-blend-screen pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side at 50% 52%, rgba(253,186,116,0.20), rgba(253,186,116,0) 55%)",
                }}
              />
              {/* Warm accent on right where the human nodes are */}
              <div
                aria-hidden
                className="absolute inset-0 mix-blend-soft-light pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 72% 32%, rgba(217,249,157,0.20), transparent 60%)",
                }}
              />
              {/* Corner vignette — focuses gaze */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(125% 95% at 50% 50%, transparent 38%, rgba(0,0,0,0.30) 80%, rgba(0,0,0,0.55) 100%)",
                }}
              />
              {/* Top sheen */}
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0) 100%)",
                }}
              />
              {/* Bottom UI integration */}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent, rgba(11,11,11,0.55) 60%, rgba(11,11,11,0.85))",
                }}
              />
              {/* Premium edge accent */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  boxShadow:
                    "inset 0 0 0 1px rgba(217,249,157,0.12), inset 0 0 60px rgba(253,186,116,0.10)",
                }}
              />
              <div className="absolute top-4 left-5 text-[10px] font-mono uppercase tracking-[0.26em] text-[#D9F99D]/95">
                ZX · Partnership Desk
              </div>
              <div className="absolute bottom-4 left-5 text-[11px] font-mono uppercase tracking-[0.22em] text-white/95">
                Building teams that compound.
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 relative rounded-3xl border border-white/10 bg-white/[0.025] p-8 sm:p-10 backdrop-blur-xl"
          >
            <div
              aria-hidden
              className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D9F99D]/60 to-transparent"
            />

            {/* Honeypot — hidden from real users, bots will fill it */}
            <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
              <label htmlFor="zx-website-hp">Website (leave blank)</label>
              <input
                id="zx-website-hp"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                testid="contact-name"
                placeholder="Your full name"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                testid="contact-email"
                placeholder="you@company.com"
              />
              <PhoneInput
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                testid="contact-phone"
              />
              <Field
                label="Company Name"
                name="company"
                value={form.company}
                onChange={onChange}
                testid="contact-company"
                placeholder="Your firm"
              />
            </div>
            <div className="mt-6">
              <label
                htmlFor="contact-message-field"
                className="block text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-300"
              >
                Message
              </label>
              <textarea
                id="contact-message-field"
                name="message"
                value={form.message}
                onChange={onChange}
                data-testid="contact-message"
                rows={5}
                placeholder="Briefly describe the role, team or mandate…"
                required
                className="mt-2 w-full bg-transparent border-b border-white/25 focus:border-[#D9F99D] focus-visible:outline-none text-white placeholder:text-neutral-500 py-3 resize-none transition-colors"
              />
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-10 flex flex-wrap items-center justify-between gap-4"
            >
              <div className="text-xs text-neutral-500 max-w-xs">
                By submitting, you agree to our engagement terms. We reply to
                every serious inquiry within 24 hours.
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                aria-busy={submitting}
                className="group inline-flex items-center gap-2 rounded-full bg-[#D9F99D] hover:bg-[#E4FBB1] text-black text-sm font-semibold px-7 py-3.5 transition-all shadow-[0_0_24px_rgba(217,249,157,0.45)] hover:shadow-[0_0_36px_rgba(217,249,157,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Let&apos;s Have a Call
                    <PhoneCall size={16} />
                  </>
                )}
              </button>
            </motion.div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, placeholder }) {
  const inputId = `field-${name}`;
  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-300"
      >
        {label}
      </label>
      <input
        id={inputId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-white/25 focus:border-[#D9F99D] focus-visible:outline-none text-white placeholder:text-neutral-500 py-3 transition-colors"
        autoComplete="off"
        required
      />
    </div>
  );
}
